import React from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { X, Info } from "@phosphor-icons/react";

/**
 * Iter209br — Tooltip di onboarding contestuale per pagina.
 *
 * Mostra un pannello flottante (in alto a destra) con titolo + descrizione
 * della pagina. Compare UNA SOLA VOLTA per utente per pagina: al dismiss
 * viene salvato `ht_op_tip_{userId}_{pageKey}` in localStorage.
 *
 * Il tooltip viene renderizzato SOLO se:
 *   - l'utente è un operatore (role.endsWith("_operator")) o supervisor
 *   - `outOnly=false` OR user.operator_type === "out"
 *   - non è già stato dismesso
 *
 * Uso:
 *   <OperatorPageTip pageKey="bacheca" title="Bacheca pratiche" body="..." />
 *   <OperatorPageTip pageKey="compensi" title="I tuoi compensi" body="..." outOnly />
 */
export default function OperatorPageTip({ pageKey, title, body, outOnly = false, actions = null }) {
  const { user } = useAuth();
  const uid = user?.id || user?._id || "anon";
  const [dismissed, setDismissed] = React.useState(() => {
    try { return !!localStorage.getItem(`ht_op_tip_${uid}_${pageKey}`); } catch { return false; }
  });

  if (!user) return null;
  const isOperator = String(user.role || "").endsWith("_operator");
  if (!isOperator) return null;
  if (outOnly && user.operator_type !== "out") return null;
  if (dismissed) return null;

  const close = () => {
    try { localStorage.setItem(`ht_op_tip_${uid}_${pageKey}`, "1"); } catch (_) {}
    setDismissed(true);
  };

  return (
    <div
      className="fixed top-20 right-4 z-[80] max-w-sm shadow-xl border border-purple-200 bg-white rounded-2xl overflow-hidden animate-in slide-in-from-right-4 duration-200"
      data-testid={`op-tip-${pageKey}`}
      role="dialog"
      aria-live="polite"
    >
      <div className="p-4">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center shrink-0 mt-0.5">
            <Info size={16} weight="fill" className="text-purple-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-sm text-slate-900" data-testid={`op-tip-${pageKey}-title`}>{title}</h3>
            <div className="mt-1 text-xs text-slate-600 leading-relaxed" data-testid={`op-tip-${pageKey}-body`}>{body}</div>
            {actions && <div className="mt-2">{actions}</div>}
          </div>
          <button onClick={close} className="p-1 rounded-md hover:bg-slate-100" aria-label="Chiudi"
            data-testid={`op-tip-${pageKey}-x`}>
            <X size={14} />
          </button>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" onClick={close}
            className="bg-purple-600 hover:bg-purple-700 text-white h-7 px-3 text-xs"
            data-testid={`op-tip-${pageKey}-dismiss`}
          >
            Ho capito
          </Button>
        </div>
      </div>
    </div>
  );
}
